import { RefObject } from "react";
import type { LogoutIntent } from "../../Auth/ssoSession";
import { getCurrentSession } from "../../Auth/AuthApi";
import { CONTEXT_KEY } from "../../Auth/AuthContext";
import { AuthState } from "../../types/AuthProvider";
import { isAmplify, isLocalhost, isDevEnv } from "../../utils";

/**
 * Generate op_id matching the Python backend format: MM_ddTHH_mmU{uuid8}
 * Used to correlate frontend requests with backend logs in Datadog.
 */
export function generateOpId(): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const now = new Date();
  return (
    `${pad(now.getUTCMonth() + 1)}_${pad(now.getUTCDate())}` +
    `T${pad(now.getUTCHours())}_${pad(now.getUTCMinutes())}` +
    `U${crypto.randomUUID().replace(/-/g, "").slice(0, 8)}`
  );
}

/**
 * Get authenticated headers with JWT Bearer token and X-Account header
 *
 * Adds:
 * - Authorization: Bearer <JWT_TOKEN> (from AWS Cognito)
 * - X-Account: <ACCOUNT_ID> (from AuthContext for multi-tenancy)
 * - X-Account: <CONTEXT_ID> (from impersonation context if active - takes precedence)
 * - x-impersonate: "true" (when impersonation context is active)
 *
 * @param authRef - Reference to AuthContext state
 * @param additionalHeaders - Optional additional headers to merge
 * @param targetAccountUuid - Optional account to scope this request to, overriding
 *   both the caller's own account and any impersonation context
 * @returns Promise<Record<string, string>> Headers object with authentication
 */
export async function getAuthHeaders(
  authRef: RefObject<AuthState>,
  additionalHeaders: Record<string, string> = {},
  targetAccountUuid?: string
): Promise<Record<string, string>> {
  // Get JWT token from AWS Cognito
  const session = await getCurrentSession();
  const token = session.getAccessToken().getJwtToken();

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...additionalHeaders,
  };

  // Add X-Account header from account (for multi-tenancy)
  const account = authRef.current?.account;
  if (account?.id) {
    headers["X-Account"] = account.id;
  }

  // Add X-Account header for impersonation context (takes precedence)
  const impersonationContext = authRef.current?.[CONTEXT_KEY];
  if (impersonationContext?.id) {
    headers["X-Account"] = impersonationContext.id;
    headers["x-impersonate"] = "true";
  }

  // Explicit target account takes highest precedence and is not impersonation
  if (targetAccountUuid) {
    headers["X-Account"] = targetAccountUuid;
    delete headers["x-impersonate"];
  }

  headers["op-id"] = headers["op-id"] ?? generateOpId();

  return headers;
}

/**
 * Permission-error message fragments (case-insensitive substring match) that
 * indicate a 403 came from a backend resource-level authorization check
 * ("you lack access to this endpoint/feature") rather than from an invalid
 * session ("your token is bad — sign out").
 *
 * Intentionally narrow — only exact FastAPI/DRF phrases observed in production:
 *   { "detail": "Insufficient permissions" }
 *   { "detail": "Permission denied" }
 *   { "message": "Permission denied for resource X" }
 *   { "errors": [{ "message": "Insufficient permissions for /ai-agents" }] }
 * Broader terms like "forbidden" are excluded to avoid matching generic HTTP
 * proxy/CDN 403 responses (e.g. "403 Forbidden") which would suppress a
 * legitimate logout.
 */
const PERMISSION_ERROR_MESSAGES = [
  "insufficient permissions",
  "permission denied",
];

function extractErrorText(payload: unknown): string {
  // eslint-disable-next-line eqeqeq
  if (payload == null) {
    return "";
  }
  if (typeof payload !== "object") {
    return "";
  }

  const obj = payload as Record<string, unknown>;
  const candidates: unknown[] = [obj.detail, obj.message, obj.error];

  // GraphQL-style: { errors: [{ message: "..." }] }
  if (Array.isArray(obj.errors)) {
    for (const err of obj.errors) {
      if (err && typeof err === "object") {
        candidates.push((err as Record<string, unknown>).message);
      }
    }
  }

  return candidates.filter((c): c is string => typeof c === "string").join(" ");
}

/**
 * Returns true when the response body looks like a resource-level permission
 * denial rather than an authentication failure. Used to suppress logout on
 * 403 responses from endpoints the user happens not to have access to (e.g.
 * `/ai-agents`) — see WAK-33496.
 */
export function isPermissionError(responseData: unknown): boolean {
  const text = extractErrorText(responseData).toLowerCase();
  if (!text) {
    return false;
  }
  return PERMISSION_ERROR_MESSAGES.some((msg) => text.includes(msg));
}

/**
 * Handle 401/403 authentication errors
 *
 * Behavior matches GraphQL Apollo Client error handling:
 * - If impersonating: Stop impersonation + dispatch storage event (shows "session expired" modal)
 * - If regular user: Logout
 *
 * 403 with a permission-error body is treated as a resource-level denial and is
 * NOT treated as an auth failure — no logout, no stopImpersonation. This
 * prevents the "random logout" experience reported in WAK-33496 when a user
 * navigates to a page they aren't entitled to (e.g. /ai-agents).
 *
 * @param status - HTTP status code
 * @param authRef - Reference to AuthContext state
 * @param logout - Logout function from AuthContext
 * @param stopImpersonation - Stop impersonation function from AuthContext
 * @param responseData - Optional parsed response body (used only for the
 *   403-permission-vs-auth distinction; 401s always trigger logout)
 */
export function handleAuthError(
  status: number,
  authRef: RefObject<AuthState>,
  logout: (options?: LogoutIntent) => void,
  stopImpersonation: () => void,
  responseData?: unknown
): void {
  // Only handle 401/403
  if (status !== 401 && status !== 403) {
    return;
  }

  // 403 with a "you lack permission to this resource" body is NOT an auth
  // failure — it's a routing/authorization issue. Skip both logout AND
  // stopImpersonation: if an admin is impersonating a user who navigates to a
  // page they don't have access to, the session should be preserved so the
  // admin can observe what the user sees (the page's own 403 error state).
  if (status === 403 && isPermissionError(responseData)) {
    return;
  }

  const contextValue = authRef.current?.[CONTEXT_KEY];
  if (contextValue?.id) {
    // User is impersonating - stop impersonation
    stopImpersonation();
    // Dispatch storage event to trigger "session expired" modal
    window.dispatchEvent(new StorageEvent("storage", { key: CONTEXT_KEY }));
  } else if (authRef.current?.user) {
    // Regular user - log them out. Not user-initiated: an expired session must
    // keep the SSO auto-redirect armed.
    logout({ userInitiated: false });
  }
}

/**
 * Check if error handling should be skipped (dev/test environments)
 *
 * Returns true for:
 * - Amplify environments
 * - Localhost
 * - Dev environment
 *
 * Matches GraphQL Apollo Client behavior
 *
 * @returns boolean True if error handling should be skipped
 */
export function shouldSkipErrorHandling(): boolean {
  return isAmplify || isLocalhost || isDevEnv;
}
